import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import {
  getClientCommunity,
  createClientCommunity,
  archiveClientCommunity,
} from "../../api/clientCommunitiesApi";
import { listClientCourses } from "../../api/clientCoursesApi";
import {
  saveCommunityDraft,
  submitCommunityForReview,
  listCommunityVersions,
} from "../../api/clientPublishingApi";
import StatusBadge from "../../components/midpoint/intelligence/StatusBadge";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../components/clientPortal/ClientPortalStates";
import { formatDateTime } from "../../components/midpoint/admin/users/adminUserFormatters";
import ClientPortalImageUpload from "../../components/clientPortal/ClientPortalImageUpload";
import ExternalLinksEditor from "../../components/clientPortal/ExternalLinksEditor";
import { resolveClientPortalAssetUrl } from "../../api/clientUploadApi";
import "../../styles/clientCommunityForm.css";

const DISPLAY_STATUSES = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "HIDDEN", label: "Hidden" },
  { value: "ARCHIVED", label: "Archived" },
];

const STATUS_TONE = {
  DRAFT: "neutral",
  PENDING_REVIEW: "warning",
  APPROVED: "info",
  REJECTED: "danger",
  PUBLISHED: "success",
  ARCHIVED: "neutral",
};

const TABS = [
  { key: "details", label: "Details", icon: "\u270F\uFE0F" },
  { key: "location", label: "Location", icon: "\uD83D\uDCCD" },
  { key: "media", label: "Media & Links", icon: "\uD83D\uDDBC\uFE0F" },
  { key: "lifestyle", label: "Lifestyle", icon: "\u2728" },
  { key: "seo", label: "SEO", icon: "\uD83D\uDD0D" },
];

const EMPTY_FORM = {
  communityName: "",
  slug: "",
  shortDescription: "",
  longDescription: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  stateRegion: "",
  postalCode: "",
  country: "US",
  displayStatus: "DRAFT",
  isFeatured: false,
  sortOrder: "",
  heroImageUrl: "",
  websiteUrl: "",
  externalLinks: [],
  hoaNotes: "",
  amenities: "",
  lifestyleTags: "",
  seoTitle: "",
  seoDescription: "",
  latitude: "",
  longitude: "",
};

function snapshotToForm(snapshot) {
  if (!snapshot) return null;
  return {
    communityName: snapshot.communityName || "",
    slug: snapshot.slug || "",
    shortDescription: snapshot.shortDescription || "",
    longDescription: snapshot.longDescription || "",
    addressLine1: snapshot.addressLine1 || "",
    addressLine2: snapshot.addressLine2 || "",
    city: snapshot.city || "",
    stateRegion: snapshot.stateRegion || "",
    postalCode: snapshot.postalCode || "",
    country: snapshot.country || "",
    displayStatus: snapshot.displayStatus || "DRAFT",
    isFeatured: Boolean(snapshot.isFeatured),
    sortOrder: snapshot.sortOrder ?? "",
    heroImageUrl: snapshot.heroImageUrl || "",
    websiteUrl: snapshot.websiteUrl || "",
    externalLinks: Array.isArray(snapshot.externalLinks) ? snapshot.externalLinks : [],
    hoaNotes: snapshot.hoaNotes || "",
    amenities: Array.isArray(snapshot.amenities) ? snapshot.amenities.join(", ") : "",
    lifestyleTags: Array.isArray(snapshot.lifestyleTags)
      ? snapshot.lifestyleTags.join(", ")
      : "",
    seoTitle: snapshot.seoTitle || "",
    seoDescription: snapshot.seoDescription || "",
    latitude: snapshot.latitude ?? "",
    longitude: snapshot.longitude ?? "",
  };
}

function communityToForm(community) {
  if (community.draftSnapshot) {
    const fromDraft = snapshotToForm(community.draftSnapshot);
    if (fromDraft) return fromDraft;
  }
  return {
    communityName: community.communityName || "",
    slug: community.slug || "",
    shortDescription: community.shortDescription || "",
    longDescription: community.longDescription || "",
    addressLine1: community.addressLine1 || "",
    addressLine2: community.addressLine2 || "",
    city: community.city || "",
    stateRegion: community.stateRegion || "",
    postalCode: community.postalCode || "",
    country: community.country || "",
    displayStatus: community.displayStatus || "DRAFT",
    isFeatured: Boolean(community.isFeatured),
    sortOrder: community.sortOrder ?? "",
    heroImageUrl: community.heroImageUrl || "",
    websiteUrl: community.websiteUrl || "",
    externalLinks: Array.isArray(community.externalLinks) ? community.externalLinks : [],
    hoaNotes: community.hoaNotes || "",
    amenities: Array.isArray(community.amenities) ? community.amenities.join(", ") : "",
    lifestyleTags: Array.isArray(community.lifestyleTags)
      ? community.lifestyleTags.join(", ")
      : "",
    seoTitle: community.seoTitle || "",
    seoDescription: community.seoDescription || "",
    latitude: community.latitude ?? "",
    longitude: community.longitude ?? "",
  };
}

function FormField({ label, required, hint, children }) {
  return (
    <label className="ccf-field">
      <span className="ccf-field__label">
        {label}
        {required && <span className="ccf-field__req">*</span>}
      </span>
      {children}
      {hint && <span className="ccf-field__hint">{hint}</span>}
    </label>
  );
}

function CharCount({ value, max }) {
  const len = (value || "").length;
  return (
    <span className={`ccf-field__charcount${len > max * 0.9 ? " ccf-field__charcount--warn" : ""}`}>
      {len}/{max}
    </span>
  );
}

function TagPills({ value, variant }) {
  if (!value) return null;
  const tags = value.split(",").map((t) => t.trim()).filter(Boolean);
  if (!tags.length) return null;
  return (
    <div className="ccf-tags">
      {tags.map((tag) => (
        <span key={tag} className={`ccf-tag ${variant === "lifestyle" ? "ccf-tag--lifestyle" : ""}`}>
          {tag}
        </span>
      ))}
    </div>
  );
}

function DetailsSection({ form, updateField }) {
  return (
    <div className="ccf-section-enter">
      <section className="ccf-card">
        <h2 className="ccf-card__title">Basic Information</h2>
        <p className="ccf-card__description">The name and descriptions shown to visitors</p>
        <div className="ccf-card__body">
          <div className="ccf-row ccf-row--2">
            <FormField label="Community name" required>
              <input value={form.communityName} onChange={updateField("communityName")} required className="ccf-input" placeholder="e.g. Southern Highlands" />
            </FormField>
            <FormField label="URL slug" hint="Leave blank to auto-generate from name">
              <input value={form.slug} onChange={updateField("slug")} placeholder="auto-from-name" className="ccf-input" />
            </FormField>
          </div>
          <FormField label="Short description" hint="Shown in cards and search results">
            <textarea value={form.shortDescription} onChange={updateField("shortDescription")} rows={2} className="ccf-input ccf-input--textarea" placeholder="A brief overview of the community..." />
          </FormField>
          <FormField label="Full description" hint="Detailed content for the community page">
            <textarea value={form.longDescription} onChange={updateField("longDescription")} rows={6} className="ccf-input ccf-input--textarea" placeholder="Describe the community, its history, features, and what makes it unique..." />
          </FormField>
        </div>
      </section>
    </div>
  );
}

function LocationSection({ form, updateField }) {
  return (
    <div className="ccf-section-enter">
      <section className="ccf-card">
        <h2 className="ccf-card__title">Address</h2>
        <p className="ccf-card__description">Street address for this community</p>
        <div className="ccf-card__body">
          <FormField label="Address line 1">
            <input value={form.addressLine1} onChange={updateField("addressLine1")} className="ccf-input" placeholder="123 Golf Club Drive" />
          </FormField>
          <FormField label="Address line 2" hint="Suite, unit, building, etc.">
            <input value={form.addressLine2} onChange={updateField("addressLine2")} className="ccf-input" placeholder="Suite 100" />
          </FormField>
          <div className="ccf-row ccf-row--2">
            <FormField label="City">
              <input value={form.city} onChange={updateField("city")} className="ccf-input" placeholder="Las Vegas" />
            </FormField>
            <FormField label="State / Region">
              <input value={form.stateRegion} onChange={updateField("stateRegion")} className="ccf-input" placeholder="Nevada" />
            </FormField>
          </div>
          <div className="ccf-row ccf-row--3">
            <FormField label="Postal code">
              <input value={form.postalCode} onChange={updateField("postalCode")} className="ccf-input" placeholder="89141" />
            </FormField>
            <FormField label="Country">
              <input value={form.country} onChange={updateField("country")} className="ccf-input" placeholder="US" />
            </FormField>
          </div>
        </div>
      </section>

      <section className="ccf-card">
        <h2 className="ccf-card__title">Coordinates</h2>
        <p className="ccf-card__description">Map position for this community</p>
        <div className="ccf-card__body">
          <div className="ccf-row ccf-row--2">
            <FormField label="Latitude" hint="Decimal degrees, e.g. 36.1699">
              <input value={form.latitude} onChange={updateField("latitude")} className="ccf-input" placeholder="36.1699" />
            </FormField>
            <FormField label="Longitude" hint="Decimal degrees, e.g. -115.1398">
              <input value={form.longitude} onChange={updateField("longitude")} className="ccf-input" placeholder="-115.1398" />
            </FormField>
          </div>
          {(form.latitude && form.longitude) && (
            <div className="ccf-map-hint">
              <span className="ccf-map-hint__icon" aria-hidden="true">&#128506;</span>
              Coordinates set: {form.latitude}, {form.longitude}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function MediaSection({ clientViewId, communityId, form, setForm, updateField }) {
  return (
    <div className="ccf-section-enter">
      <section className="ccf-card">
        <h2 className="ccf-card__title">Media & Links</h2>
        <p className="ccf-card__description">Upload a hero photo and add helpful community links</p>
        <div className="ccf-card__body">
          <ClientPortalImageUpload
            clientViewId={clientViewId}
            kind="community_hero"
            entityId={communityId}
            label="Hero image"
            hint="Recommended size: 1200 × 600px. JPG, PNG, or WebP up to 8MB."
            value={form.heroImageUrl}
            onChange={(heroImageUrl) => setForm((prev) => ({ ...prev, heroImageUrl }))}
          />
          {form.heroImageUrl ? (
            <div className="ccf-image-preview">
              <img
                src={resolveClientPortalAssetUrl(form.heroImageUrl)}
                alt="Hero preview"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
          ) : null}
          <FormField label="Primary website" hint="Main community or developer website">
            <input value={form.websiteUrl} onChange={updateField("websiteUrl")} type="url" className="ccf-input" placeholder="https://example.com" />
          </FormField>
          <ExternalLinksEditor
            links={form.externalLinks}
            onChange={(externalLinks) => setForm((prev) => ({ ...prev, externalLinks }))}
          />
        </div>
      </section>
    </div>
  );
}

function LifestyleSection({ form, updateField }) {
  return (
    <div className="ccf-section-enter">
      <section className="ccf-card">
        <h2 className="ccf-card__title">Amenities & Lifestyle</h2>
        <p className="ccf-card__description">Tags and features that describe this community</p>
        <div className="ccf-card__body">
          <FormField label="Amenities" hint="Separate with commas">
            <input value={form.amenities} onChange={updateField("amenities")} className="ccf-input" placeholder="Pool, Tennis, Clubhouse, Fitness Center..." />
          </FormField>
          <TagPills value={form.amenities} />
          <FormField label="Lifestyle tags" hint="Separate with commas">
            <input value={form.lifestyleTags} onChange={updateField("lifestyleTags")} className="ccf-input" placeholder="Golf, Active Adult, Luxury, Gated..." />
          </FormField>
          <TagPills value={form.lifestyleTags} variant="lifestyle" />
          <FormField label="HOA notes" hint="HOA details, fees, or governance info">
            <textarea value={form.hoaNotes} onChange={updateField("hoaNotes")} rows={3} className="ccf-input ccf-input--textarea" placeholder="Monthly HOA fees, included services, special assessments..." />
          </FormField>
        </div>
      </section>
    </div>
  );
}

function SeoSection({ form, updateField }) {
  return (
    <div className="ccf-section-enter">
      <section className="ccf-card">
        <h2 className="ccf-card__title">Search Engine Optimization</h2>
        <p className="ccf-card__description">Control how this community appears in search results</p>
        <div className="ccf-card__body">
          <FormField label="SEO title" hint="Appears as the browser tab title and in search results">
            <input value={form.seoTitle} onChange={updateField("seoTitle")} maxLength={120} className="ccf-input" placeholder="e.g. Southern Highlands Golf Community | Golf Homes Las Vegas" />
            <CharCount value={form.seoTitle} max={120} />
          </FormField>
          <FormField label="SEO description" hint="The meta description shown in search engines">
            <textarea value={form.seoDescription} onChange={updateField("seoDescription")} rows={3} maxLength={320} className="ccf-input ccf-input--textarea" placeholder="Describe the community for search engines..." />
            <CharCount value={form.seoDescription} max={320} />
          </FormField>
        </div>
      </section>
    </div>
  );
}

const ClientCommunityFormPage = () => {
  const { clientViewId, communityId } = useParams();
  const isNew = !communityId;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [canManage, setCanManage] = useState(isNew);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [publishing, setPublishing] = useState(null);
  const [versions, setVersions] = useState([]);
  const [relatedCourses, setRelatedCourses] = useState([]);
  const [activeTab, setActiveTab] = useState("details");
  const paths = useClientPortalPaths();

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    getClientCommunity(clientViewId, communityId)
      .then(async (body) => {
        setForm(communityToForm(body.community));
        setCanManage(body.canManage);
        setPublishing(body.community.publishing || null);
        try {
          const [versionsBody, coursesBody] = await Promise.all([
            listCommunityVersions(clientViewId, communityId),
            listClientCourses(clientViewId, { communityId, pageSize: 10 }).catch(() => ({
              courses: [],
            })),
          ]);
          setVersions(versionsBody.versions || []);
          setRelatedCourses(coursesBody.courses || []);
        } catch {
          setVersions([]);
        }
      })
      .catch((error) => {
        setErrorMessage(error.message || "Failed to load community.");
      })
      .finally(() => setLoading(false));
  }, [clientViewId, communityId, isNew]);

  const updateField = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => ({
    ...form,
    isFeatured: form.isFeatured,
    sortOrder: form.sortOrder === "" ? null : Number(form.sortOrder),
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isNew) return handleSaveDraft();
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const result = await createClientCommunity(clientViewId, buildPayload());
      setSuccessMessage("Community created.");
      navigate(paths.viewSection(clientViewId, "communities", result.community.communityId, "edit"), {
        replace: true,
      });
    } catch (error) {
      setErrorMessage(error.message || "Failed to save community.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const result = await saveCommunityDraft(clientViewId, communityId, buildPayload());
      setPublishing(result.publishing || null);
      setSuccessMessage("Draft saved. Changes are not public until published.");
    } catch (error) {
      setErrorMessage(error.message || "Failed to save draft.");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!window.confirm("Submit this community for internal review?")) return;
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await saveCommunityDraft(clientViewId, communityId, buildPayload());
      const result = await submitCommunityForReview(clientViewId, communityId);
      setPublishing(result.publishing || null);
      setSuccessMessage("Submitted for review.");
    } catch (error) {
      setErrorMessage(error.message || "Failed to submit for review.");
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm("Archive this community? This removes it from public view.")) return;
    setSaving(true);
    try {
      await archiveClientCommunity(clientViewId, communityId);
      navigate(paths.viewSection(clientViewId, "communities"));
    } catch (error) {
      setErrorMessage(error.message || "Failed to archive community.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ClientPortalLoading message="Loading community..." />;

  if (!canManage && !isNew) {
    return <ClientPortalError message="You have read-only access to communities." />;
  }

  const canSubmitReview =
    publishing &&
    canManage &&
    !publishing.hasPendingReview &&
    (publishing.hasDraftChanges || publishing.publishStatus === "DRAFT" || publishing.publishStatus === "REJECTED");

  const publishHint =
    publishing?.publishStatus === "REJECTED"
      ? "Update your draft and submit again for review."
      : publishing?.publishStatus === "PENDING_REVIEW"
        ? "Your submission is awaiting internal review."
        : publishing?.publishStatus === "APPROVED"
          ? "Approved \u2014 waiting for an admin to publish."
          : publishing?.publishStatus === "PUBLISHED" && publishing?.hasDraftChanges
            ? "You have unpublished draft changes."
            : publishing?.publishStatus === "DRAFT" || publishing?.hasDraftChanges
              ? "Save a draft, then submit for review when ready."
              : null;

  const tabContent = {
    details: <DetailsSection form={form} updateField={updateField} />,
    location: <LocationSection form={form} updateField={updateField} />,
    media: (
      <MediaSection
        clientViewId={clientViewId}
        communityId={communityId}
        form={form}
        setForm={setForm}
        updateField={updateField}
      />
    ),
    lifestyle: <LifestyleSection form={form} updateField={updateField} />,
    seo: <SeoSection form={form} updateField={updateField} />,
  };

  return (
    <div className="ccf-layout">
      {/* Header */}
      <header className="ccf-header">
        <div className="ccf-header__left">
          <nav className="ccf-breadcrumb">
            <Link to={paths.viewSection(clientViewId, "communities")}>Communities</Link>
            <span className="ccf-breadcrumb__sep" aria-hidden="true">/</span>
            <span>{isNew ? "New Community" : form.communityName || "Edit"}</span>
          </nav>
          <h1 className="ccf-header__title">
            {isNew ? "Add Community" : form.communityName || "Edit Community"}
          </h1>
          {!isNew && form.slug && (
            <p className="ccf-header__subtitle">/{form.slug}</p>
          )}
        </div>
        <div className="ccf-header__actions">
          <Link to={paths.viewSection(clientViewId, "communities")} className="ccf-btn ccf-btn--ghost">
            Cancel
          </Link>
          <button
            type="button"
            className="ccf-btn ccf-btn--primary"
            disabled={saving}
            onClick={isNew ? handleSubmit : handleSaveDraft}
          >
            {saving ? "Saving..." : isNew ? "Create Community" : "Save Draft"}
          </button>
        </div>
      </header>

      {/* Toast messages */}
      {successMessage && (
        <div className="ccf-toast ccf-toast--success" role="status">
          <span className="ccf-toast__icon" aria-hidden="true">&#10003;</span>
          {successMessage}
          <button className="ccf-toast__close" onClick={() => setSuccessMessage("")} aria-label="Dismiss">&times;</button>
        </div>
      )}
      {errorMessage && (
        <div className="ccf-toast ccf-toast--error" role="alert">
          <span className="ccf-toast__icon" aria-hidden="true">!</span>
          {errorMessage}
          <button className="ccf-toast__close" onClick={() => setErrorMessage("")} aria-label="Dismiss">&times;</button>
        </div>
      )}

      {/* Tab navigation */}
      <nav className="ccf-tabs" aria-label="Form sections">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`ccf-tab ${activeTab === tab.key ? "ccf-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
            aria-selected={activeTab === tab.key}
          >
            <span className="ccf-tab__icon" aria-hidden="true">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Two-column body */}
      <div className="ccf-body">
        {/* Left: Active tab content */}
        <form onSubmit={handleSubmit} className="ccf-main" id="community-form">
          {tabContent[activeTab]}
        </form>

        {/* Right: Sidebar */}
        <aside className="ccf-sidebar">
          {/* Status & Visibility */}
          <section className="ccf-card ccf-card--sidebar">
            <h2 className="ccf-card__title">Status & Visibility</h2>
            <div className="ccf-card__body">
              <FormField label="Display status">
                <select value={form.displayStatus} onChange={updateField("displayStatus")} className="ccf-input ccf-input--select">
                  {DISPLAY_STATUSES.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </FormField>
              <label className="ccf-toggle">
                <input type="checkbox" checked={form.isFeatured} onChange={updateField("isFeatured")} />
                <span className="ccf-toggle__track" />
                <span className="ccf-toggle__label">
                  Featured community
                  <span className="ccf-toggle__sublabel">Show prominently on the site</span>
                </span>
              </label>
              <FormField label="Sort order" hint="Lower numbers appear first">
                <input type="number" value={form.sortOrder} onChange={updateField("sortOrder")} className="ccf-input" placeholder="0" />
              </FormField>
            </div>
          </section>

          {/* Publishing */}
          {!isNew && publishing && (
            <section className="ccf-card ccf-card--sidebar">
              <div className="ccf-card__title-row">
                <h2 className="ccf-card__title">Publishing</h2>
                <StatusBadge
                  tone={STATUS_TONE[publishing.publishStatus] || "neutral"}
                  label={publishing.publishStatusLabel || publishing.publishStatus}
                />
              </div>
              <div className="ccf-card__body">
                {publishHint && <p className="ccf-publish-hint">{publishHint}</p>}

                {publishing.rejectionComments && (
                  <div className="ccf-rejection">
                    <strong>Review feedback</strong>
                    <p>{publishing.rejectionComments}</p>
                  </div>
                )}

                <dl className="ccf-publish-meta">
                  {publishing.lastSubmittedAt && (
                    <div><dt>Submitted</dt><dd>{formatDateTime(publishing.lastSubmittedAt)}</dd></div>
                  )}
                  {publishing.lastReviewedAt && (
                    <div><dt>Reviewed</dt><dd>{formatDateTime(publishing.lastReviewedAt)}</dd></div>
                  )}
                  {publishing.publishedAt && (
                    <div><dt>Published</dt><dd>{formatDateTime(publishing.publishedAt)}</dd></div>
                  )}
                </dl>

                {canManage && (
                  <div className="ccf-publish-actions">
                    <button
                      type="button"
                      className="ccf-btn ccf-btn--primary ccf-btn--full"
                      disabled={saving || publishing.hasPendingReview}
                      onClick={handleSaveDraft}
                    >
                      {saving ? "Saving..." : "Save Draft"}
                    </button>
                    <button
                      type="button"
                      className="ccf-btn ccf-btn--outline ccf-btn--full"
                      disabled={saving || !canSubmitReview}
                      onClick={handleSubmitReview}
                    >
                      Submit for Review
                    </button>
                    <Link
                      to={paths.viewSection(clientViewId, "communities", communityId, "preview")}
                      className="ccf-btn ccf-btn--ghost ccf-btn--full"
                    >
                      Preview Draft
                    </Link>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Related Courses */}
          {!isNew && relatedCourses.length > 0 && (
            <section className="ccf-card ccf-card--sidebar">
              <h2 className="ccf-card__title">Golf Courses ({relatedCourses.length})</h2>
              <div className="ccf-card__body">
                {relatedCourses.map((course) => (
                  <div key={course.courseId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                    <div>
                      <span style={{ fontSize: "13px", fontWeight: 600 }}>{course.courseName}</span>
                      {(course.numberOfHoles || course.par) && (
                        <span style={{ fontSize: "12px", color: "var(--cp-muted, #6b7280)", marginLeft: "6px" }}>
                          {course.numberOfHoles ? `${course.numberOfHoles}H` : ""}
                          {course.par ? ` Par ${course.par}` : ""}
                        </span>
                      )}
                    </div>
                    <Link
                      to={paths.viewSection(clientViewId, "courses", course.courseId, "edit")}
                      className="ccf-btn ccf-btn--sm ccf-btn--outline"
                    >
                      Edit
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Version History */}
          {!isNew && versions.length > 0 && (
            <section className="ccf-card ccf-card--sidebar">
              <h2 className="ccf-card__title">Version History</h2>
              <div className="ccf-card__body">
                <ul className="ccf-versions">
                  {versions.map((v) => (
                    <li key={v.contentVersionId} className="ccf-versions__item">
                      <span className="ccf-versions__num">v{v.versionNumber}</span>
                      <div className="ccf-versions__detail">
                        <span className="ccf-versions__status">{v.publishStatusLabel}</span>
                        {v.submittedAt && <span className="ccf-versions__date">Submitted {formatDateTime(v.submittedAt)}</span>}
                        {v.publishedAt && <span className="ccf-versions__date">Published {formatDateTime(v.publishedAt)}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Danger Zone */}
          {!isNew && canManage && (
            <section className="ccf-card ccf-card--sidebar ccf-card--danger">
              <h2 className="ccf-card__title">Danger Zone</h2>
              <div className="ccf-card__body">
                <p className="ccf-danger-text">Archiving removes this community from public view. This action can be reversed by an admin.</p>
                <button
                  type="button"
                  className="ccf-btn ccf-btn--danger ccf-btn--full"
                  disabled={saving}
                  onClick={handleArchive}
                >
                  Archive Community
                </button>
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
};

export default ClientCommunityFormPage;
